import React, {cloneElement, isValidElement} from 'react'
import propTypes from 'prop-types'
import {isNil} from 'lodash'
import MuiButton from '@material-ui/core/Button'
import IconButton from 'material-ui/IconButton'
import themeVars from "theme/themeVars"
import Icon from '../../components/icon'
import {prefix} from 'helpers/component'
import {t} from "helpers/language"
import './button.scss'

const BORDER_RADIUS = 3
const classPrefix = 'button'
const getClassName = prefix(classPrefix + '-')

const getStyles = (props) => {
  const {
    isLeft,
    isRight,
    size,
    width,
    disabled,
    flat,
    primary,
    secondary,
  } = props
  let {color, backgroundColor} = props
  if (!color) color = themeVars.palette.primaryFontColor

  let marginLeft = -2, marginRight = 0
  let borderTopLeftRadius, borderTopRightRadius, borderBottomLeftRadius, borderBottomRightRadius
  if (isLeft) {
    borderTopLeftRadius = BORDER_RADIUS
    borderBottomLeftRadius = BORDER_RADIUS
    marginLeft = 3
  }
  if (isRight) {
    borderTopRightRadius = BORDER_RADIUS
    borderBottomRightRadius = BORDER_RADIUS
    marginRight = 3
  }

  if (disabled) {
    color = themeVars.palette.disabledColor
  } else if (primary) {
    backgroundColor = themeVars.palette.primary1Color
    color = flat ? backgroundColor : 'var(--cg-color-static-white)'
  } else if (secondary) {
    backgroundColor = themeVars.palette.accent1Color
    color = flat ? backgroundColor : 'var(--cg-color-static-white)'
  }

  let iconButtonStyle
  if (!flat) {
    iconButtonStyle = {
      border: `1px solid ${themeVars.palette.borderColor}`,
      backgroundColor,
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
    }
  }

  return {
    iconButton: {
      width: size * 2,
      height: size * 2,
      marginLeft,
      marginRight,
      padding: 0,
      zIndex: null,
      ...iconButtonStyle,
    },
    iconButtonIcon: {},
    raisedButton: {
      backgroundColor,
      border: `1px solid ${themeVars.palette.borderColor}`,
      boxShadow: 'none',
      height: 36,
      lineHeight: '36px',
      marginLeft: marginLeft + 1,
      marginRight,
      padding: '0 16px',
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
      whiteSpace: 'nowrap',
      width,
    },
    raisedButtonLabel: {
      color,
    },
    flatButton: {
      fontSize: 14,
      height: 36,
      lineHeight: '36px',
      minWidth: 88,
      padding: '0 16px',
      width,
    },
    flatButtonLabel: {
      color,
    },
  }
}

const Button = (props) => {
  let {iconColor, color, tooltip} = props
  const {labelColor} = props
  const styles = getStyles(props)
  if (labelColor) styles.raisedButtonLabel.color = labelColor
  if (labelColor) styles.flatButtonLabel.color = labelColor
  const {
    className,
    disabled,
    disableFocusRipple,
    disableTouchRipple,
    flat,
    icon,
    iconType,
    label,
    labelPosition,
    lightIconColor,
    noFill,
    onClick,
    primary,
    secondary,
    size,
    style,
    title,
    tooltipPosition,
  } = props

  const disabledColor = themeVars.palette.disabledColor
  if (primary) color = themeVars.palette.primary1Color
  if (secondary) color = themeVars.palette.accent1Color
  if (disabled) {
    color = disabledColor
    iconColor = disabledColor
    tooltip = null
  } else if (!flat && isNil(iconColor)) {
    iconColor = styles.raisedButtonLabel.color
  }

  if (label) {
    let iconEl = null
    const isLabelBefore = labelPosition === 'before'
    const iconMargin = isLabelBefore
      ? {marginRight: 0, marginLeft: 12}
      : {marginLeft: 0, marginRight: 12}

    if (icon) {
      iconEl = isValidElement(icon)
        ? cloneElement(
          icon,
          {
            ...(iconColor && !icon.props.color ? {color: iconColor} : {}),
            style: {...iconMargin, ...icon.props.style},
          }
        )
        : (
          <Icon
            color={iconColor}
            lightColor={lightIconColor}
            name={icon}
            noFill={noFill}
            size={size}
            style={iconMargin}
            type={iconType}
          />
        )
    }
    if (flat) {
      return (
        <MuiButton
          className={className}
          disabled={Boolean(disabled)}
          onClick={onClick}
          style={{...styles.flatButton, ...style, color: styles.flatButtonLabel.color}}
          variant="text"
        >
          {!isLabelBefore && iconEl}
          {t(label)}
          {isLabelBefore && iconEl}
        </MuiButton>
      )
    } else {
      return (
        <MuiButton
          className={className}
          disabled={Boolean(disabled)}
          disableRipple={disableFocusRipple || disableTouchRipple}
          onClick={onClick}
          style={{...styles.raisedButton, ...style, color: styles.raisedButtonLabel.color}}
          variant="contained"
        >
          {!isLabelBefore && iconEl}
          {label}
          {isLabelBefore && iconEl}
        </MuiButton>
      )
    }
  } else {
    let iconEl = null
    if (isNil(iconColor)) iconColor = color
    if (icon) {
      iconEl = isValidElement(icon)
        ? icon
        : <Icon
          color={iconColor}
          lightColor={lightIconColor}
          name={icon}
          size={size}
          style={styles.iconButtonIcon}
        />
    }
    const classes = ['iconButton']
    if (disabled) classes.push('iconButton-disabled')
    if (flat) {
      classes.push('iconButton-flat')
    } else {
      classes.push('iconButton-raised')
    }
    return (
      <IconButton
        className={`${getClassName(classes)} ${className}`}
        disabled={Boolean(disabled)}
        onClick={onClick}
        style={styles.iconButton}
        title={title}
        tooltip={tooltip}
        tooltipPosition={tooltipPosition}
      >
        {iconEl}
      </IconButton>
    )
  }
}

Button.defaultProps = {
  backgroundColor: themeVars.palette.whiteColor,
  disableFocusRipple: false,
  disableTouchRipple: false,
  iconColor: null,
  isLeft: true,
  isRight: true,
  label: null,
  labelPosition: null,
  size: 18,
}

// Note: you should specify either icon or label, or both
Button.propTypes = {
  backgroundColor: propTypes.string,
  className: propTypes.string,
  color: propTypes.string,
  disabled: propTypes.bool,
  disableFocusRipple: propTypes.bool,
  disableTouchRipple: propTypes.bool,
  flat: propTypes.bool,
  icon: propTypes.oneOfType([
    propTypes.string,
    propTypes.element,
  ]),
  iconColor: propTypes.string,
  iconType: propTypes.string,
  isLeft: propTypes.bool,
  isRight: propTypes.bool,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.element,
  ]),
  labelColor: propTypes.string,
  labelPosition: propTypes.string,
  lightIconColor: propTypes.bool,
  noFill: propTypes.bool,
  onClick: propTypes.func,
  primary: propTypes.bool,
  secondary: propTypes.bool,
  size: propTypes.number,
  style: propTypes.object,
  title: propTypes.string,
  tooltip: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
  ]),
  tooltipPosition: propTypes.string,  // see http://www.material-ui.com/#/components/icon-button
  width: propTypes.oneOfType([
    propTypes.number,
    propTypes.string,
  ]),
}

export default Button
